// components/LogCallModal.jsx - UPDATED with dropdown
import React, { useState } from "react";
import {
  X,
  Phone,
  PhoneOff,
  PhoneMissed,
  PhoneForwarded,
  AlertCircle,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  ChevronDown,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const LogCallModal = ({ lead, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    outcome: "connected",
    duration: "",
    notes: "",
    nextAction: "next_step",
    callbackDate: "",
    callbackTime: "",
    disqualificationReason: "",
    contestReason: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showActionDropdown, setShowActionDropdown] = useState(false);

  const outcomes = [
    { value: "connected", label: "Connected", icon: Phone, color: "green" },
    {
      value: "no_answer",
      label: "No Answer",
      icon: PhoneMissed,
      color: "orange",
    },
    {
      value: "voicemail",
      label: "Voicemail",
      icon: PhoneForwarded,
      color: "blue",
    },
    {
      value: "wrong_number",
      label: "Wrong Number",
      icon: PhoneOff,
      color: "red",
    },
    { value: "busy", label: "Busy", icon: Phone, color: "yellow" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate contest reason if contesting
    if (
      formData.nextAction === "contest_lead" &&
      !formData.contestReason.trim()
    ) {
      setError("Please provide a reason for contesting this lead.");
      return;
    }

    setLoading(true);

    try {
      await apiService.logSalesEngagementCall(lead.id, formData);
      onSuccess();
    } catch (error) {
      console.error("Error logging call:", error);
      setError(error.response?.data?.message || "Failed to log call");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700/50"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`border-b p-6 ${
            isDarkMode
              ? "bg-gradient-to-r from-[#0C0E12] via-[#0E1015] to-[#0C0E12] border-gray-800/50"
              : "bg-gradient-to-r from-gray-50 via-white to-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2
                className={`text-xl font-semibold mb-1 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Log Call
              </h2>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {lead.fullName || lead.email}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-all ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700/50 text-gray-400 hover:text-white"
                  : "bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-500 hover:text-gray-700"
              }`}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div
                className={`rounded-xl p-4 ${
                  isDarkMode
                    ? "bg-red-500/10 border border-red-500/30"
                    : "bg-red-50 border border-red-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <AlertCircle
                    className={`flex-shrink-0 mt-0.5 ${
                      isDarkMode ? "text-red-400" : "text-red-600"
                    }`}
                    size={20}
                  />
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-red-300" : "text-red-600"
                    }`}
                  >
                    {error}
                  </p>
                </div>
              </div>
            )}

            {/* Call Outcome */}
            <div>
              <label
                className={`block text-sm font-medium mb-3 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Call Outcome <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {outcomes.map((outcome) => (
                  <button
                    key={outcome.value}
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, outcome: outcome.value })
                    }
                    className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                      formData.outcome === outcome.value
                        ? isDarkMode
                          ? `border-${outcome.color}-500/50 bg-${outcome.color}-500/10`
                          : `border-${outcome.color}-500 bg-${outcome.color}-50`
                        : isDarkMode
                          ? "border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50"
                          : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <outcome.icon
                      size={20}
                      className={
                        formData.outcome === outcome.value
                          ? isDarkMode
                            ? `text-${outcome.color}-400`
                            : `text-${outcome.color}-600`
                          : isDarkMode
                            ? "text-gray-400"
                            : "text-gray-400"
                      }
                    />
                    <span
                      className={
                        formData.outcome === outcome.value
                          ? isDarkMode
                            ? "text-white"
                            : "text-gray-900"
                          : isDarkMode
                            ? "text-gray-300"
                            : "text-gray-600"
                      }
                    >
                      {outcome.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Duration (seconds)
              </label>
              <div className="relative">
                <Clock
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                  size={20}
                />
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) =>
                    setFormData({ ...formData, duration: e.target.value })
                  }
                  className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                    isDarkMode
                      ? "bg-gray-900/50 border-gray-700/50 text-white placeholder-gray-500 focus:border-blue-500/50"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                  }`}
                  placeholder="e.g., 120"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Call Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows={4}
                className={`w-full px-4 py-3 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  isDarkMode
                    ? "bg-gray-900/50 border-gray-700/50 text-white placeholder-gray-500 focus:border-blue-500/50"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                }`}
                placeholder="What was discussed?"
              />
            </div>

            {/* Next Action */}
            <div>
              <label
                className={`block text-sm font-medium mb-3 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Next Action <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      nextAction: "next_step",
                      disqualificationReason: "",
                      contestReason: "",
                    })
                  }
                  className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                    formData.nextAction === "next_step"
                      ? isDarkMode
                        ? "border-blue-500/50 bg-blue-500/10"
                        : "border-blue-500 bg-blue-50"
                      : isDarkMode
                        ? "border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50"
                        : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <PhoneForwarded
                    size={20}
                    className={
                      formData.nextAction === "next_step"
                        ? isDarkMode
                          ? "text-blue-400"
                          : "text-blue-600"
                        : isDarkMode
                          ? "text-gray-400"
                          : "text-gray-400"
                    }
                  />
                  <div className="flex-1 text-left">
                    <div
                      className={
                        formData.nextAction === "next_step"
                          ? isDarkMode
                            ? "text-white font-medium"
                            : "text-gray-900 font-medium"
                          : isDarkMode
                            ? "text-gray-300"
                            : "text-gray-600"
                      }
                    >
                      Move to Next Step
                    </div>
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Progress through workflow
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      nextAction: "qualified",
                      disqualificationReason: "",
                      contestReason: "",
                    })
                  }
                  className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                    formData.nextAction === "qualified"
                      ? isDarkMode
                        ? "border-green-500/50 bg-green-500/10"
                        : "border-green-500 bg-green-50"
                      : isDarkMode
                        ? "border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50"
                        : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <CheckCircle
                    size={20}
                    className={
                      formData.nextAction === "qualified"
                        ? isDarkMode
                          ? "text-green-400"
                          : "text-green-600"
                        : isDarkMode
                          ? "text-gray-400"
                          : "text-gray-400"
                    }
                  />
                  <div className="flex-1 text-left">
                    <div
                      className={
                        formData.nextAction === "qualified"
                          ? isDarkMode
                            ? "text-white font-medium"
                            : "text-gray-900 font-medium"
                          : isDarkMode
                            ? "text-gray-300"
                            : "text-gray-600"
                      }
                    >
                      Mark as Qualified
                    </div>
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Ready for sales
                    </div>
                  </div>
                </button>

                {/* Dropdown for Unqualified / Call Back Later */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowActionDropdown(!showActionDropdown)}
                    className={`w-full flex items-center justify-between gap-3 p-4 rounded-xl border transition-all ${
                      formData.nextAction === "unqualified" ||
                      formData.nextAction === "call_back_later" ||
                      formData.nextAction === "contest_lead"
                        ? isDarkMode
                          ? formData.nextAction === "contest_lead"
                            ? "border-amber-500/50 bg-amber-500/10"
                            : "border-red-500/50 bg-red-500/10"
                          : formData.nextAction === "contest_lead"
                            ? "border-amber-500 bg-amber-50"
                            : "border-red-500 bg-red-50"
                        : isDarkMode
                          ? "border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50"
                          : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {formData.nextAction === "contest_lead" ? (
                        <AlertCircle
                          size={20}
                          className={
                            isDarkMode ? "text-amber-400" : "text-amber-600"
                          }
                        />
                      ) : (
                        <XCircle
                          size={20}
                          className={
                            formData.nextAction === "unqualified" ||
                            formData.nextAction === "call_back_later"
                              ? isDarkMode
                                ? "text-red-400"
                                : "text-red-600"
                              : isDarkMode
                                ? "text-gray-400"
                                : "text-gray-400"
                          }
                        />
                      )}
                      <div className="text-left">
                        <div
                          className={
                            formData.nextAction === "unqualified" ||
                            formData.nextAction === "call_back_later" ||
                            formData.nextAction === "contest_lead"
                              ? isDarkMode
                                ? "text-white font-medium"
                                : "text-gray-900 font-medium"
                              : isDarkMode
                                ? "text-gray-300"
                                : "text-gray-600"
                          }
                        >
                          {formData.nextAction === "contest_lead"
                            ? "Contest Lead"
                            : "Not Interested"}
                        </div>
                        <div
                          className={`text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          {formData.nextAction === "contest_lead"
                            ? "Disputing unqualified status"
                            : "Choose action"}
                        </div>
                      </div>
                    </div>
                    <ChevronDown
                      size={18}
                      className={`transition-transform ${
                        showActionDropdown ? "rotate-180" : ""
                      } ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                  </button>

                  {showActionDropdown && (
                    <div
                      className={`absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden shadow-xl z-10 border ${
                        isDarkMode
                          ? "bg-[#1a1d24] border-gray-700/50"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            nextAction: "unqualified",
                            contestReason: "",
                          });
                          setShowActionDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 p-3 transition-colors text-left ${
                          isDarkMode
                            ? "hover:bg-gray-800/50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <XCircle size={18} className="text-red-500" />
                        <div>
                          <div
                            className={`font-medium ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            Mark as Unqualified
                          </div>
                          <div
                            className={`text-xs ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Permanently unqualified
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            nextAction: "contest_lead",
                            disqualificationReason: "",
                          });
                          setShowActionDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 p-3 transition-colors text-left border-t ${
                          isDarkMode
                            ? "hover:bg-gray-800/50 border-gray-700/50"
                            : "hover:bg-gray-50 border-gray-200"
                        }`}
                      >
                        <AlertCircle size={18} className="text-amber-500" />
                        <div>
                          <div
                            className={`font-medium ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            Contest Lead
                          </div>
                          <div
                            className={`text-xs ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Dispute unqualified status
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            nextAction: "call_back_later",
                            contestReason: "",
                            disqualificationReason: "",
                          });
                          setShowActionDropdown(false);
                        }}
                        className={`w-full flex items-center gap-3 p-3 transition-colors text-left border-t ${
                          isDarkMode
                            ? "hover:bg-gray-800/50 border-gray-700/50"
                            : "hover:bg-gray-50 border-gray-200"
                        }`}
                      >
                        <Calendar size={18} className="text-indigo-500" />
                        <div>
                          <div
                            className={`font-medium ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            Call Back Later
                          </div>
                          <div
                            className={`text-xs ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            Schedule a callback
                          </div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Callback Date/Time (if call_back_later) */}
            {formData.nextAction === "call_back_later" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Callback Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.callbackDate}
                    onChange={(e) =>
                      setFormData({ ...formData, callbackDate: e.target.value })
                    }
                    required={formData.nextAction === "call_back_later"}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                      isDarkMode
                        ? "bg-gray-900/50 border-gray-700/50 text-white focus:border-blue-500/50"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Callback Time
                  </label>
                  <input
                    type="time"
                    value={formData.callbackTime}
                    onChange={(e) =>
                      setFormData({ ...formData, callbackTime: e.target.value })
                    }
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                      isDarkMode
                        ? "bg-gray-900/50 border-gray-700/50 text-white focus:border-blue-500/50"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    }`}
                  />
                </div>
              </div>
            )}

            {/* Disqualification Reason (if unqualified) */}
            {formData.nextAction === "unqualified" && (
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Disqualification Reason{" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.disqualificationReason}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      disqualificationReason: e.target.value,
                    })
                  }
                  required={formData.nextAction === "unqualified"}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                    isDarkMode
                      ? "bg-gray-900/50 border-gray-700/50 text-white focus:border-blue-500/50"
                      : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                  }`}
                >
                  <option value="">Select reason...</option>
                  <option value="not_interested">Not Interested</option>
                  <option value="budget">Budget Constraints</option>
                  <option value="already_have_solution">
                    Already Have Solution
                  </option>
                  <option value="wrong_contact">Wrong Contact</option>
                  <option value="no_authority">No Decision Authority</option>
                  <option value="timing_not_right">Timing Not Right</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}

            {/* Contest Lead Reason (if contest_lead) */}
            {formData.nextAction === "contest_lead" && (
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Contest Reason <span className="text-red-500">*</span>
                </label>
                <div
                  className={`flex items-start gap-3 p-3 rounded-xl mb-3 ${
                    isDarkMode
                      ? "bg-amber-500/10 border border-amber-500/20"
                      : "bg-amber-50 border border-amber-200"
                  }`}
                >
                  <AlertCircle
                    size={16}
                    className={`flex-shrink-0 mt-0.5 ${
                      isDarkMode ? "text-amber-400" : "text-amber-600"
                    }`}
                  />
                  <p
                    className={`text-xs ${
                      isDarkMode ? "text-amber-300" : "text-amber-700"
                    }`}
                  >
                    Contest MVF Lead
                  </p>
                </div>
                <textarea
                  value={formData.contestReason}
                  onChange={(e) =>
                    setFormData({ ...formData, contestReason: e.target.value })
                  }
                  rows={4}
                  required={formData.nextAction === "contest_lead"}
                  placeholder="Describe why this lead should be reconsidered..."
                  className={`w-full px-4 py-3 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 ${
                    isDarkMode
                      ? "bg-gray-900/50 border-amber-500/30 text-white placeholder-gray-500 focus:border-amber-500/50"
                      : "bg-white border-amber-300 text-gray-900 placeholder-gray-400 focus:border-amber-500"
                  }`}
                />
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div
          className={`border-t p-6 ${
            isDarkMode
              ? "border-gray-700/50 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-5 py-3 rounded-xl font-medium transition-all ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-800 border border-gray-700/50 text-gray-300 hover:text-white"
                  : "bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 hover:text-gray-900"
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex-1 px-5 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  <span>Logging Call...</span>
                </>
              ) : (
                <>
                  <Phone size={18} />
                  <span>Log Call</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogCallModal;
